import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import { AppState, dataActions } from 'reducers';
import store from 'store';
import { AdditionalProductGrouped, CartGroup, Product } from 'lib/types';
import './AdditionalAssortment.scss';
import { cartMethods } from 'business_logic';
import { useNotification } from 'components/NotificationComponent/NotificationComponent';
import { getProductWithPrice } from 'lib/communication/orders';
import useCartMessage from 'lib/hooks/useCartMessage';
import { Collapse, Container, Image, Nav, Navbar, NavDropdown, OverlayTrigger, Popover } from 'react-bootstrap';
import { FaCaretDown, FaCaretUp } from 'react-icons/fa';
import ButtonComponent from 'components/ButtonComponent';
import { buildResourceLink } from 'lib/communication';
import AdditionalAssortmentRow from './AdditionalAssortmentRow';
import Pagination from 'components/Pagination';
import MenuScroll from 'components/MenuScroll';
import { Messages } from 'reducers/data';
import { useDevice } from 'lib/hooks/useDevice';

interface AdditionalAssortmentProps {
    selectedProduct: Product,
    isExpanded: boolean,
    setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>,
    selectedMainGroup: string | null,
    setSelectedMainGroup: React.Dispatch<React.SetStateAction<string | null>>,
    selectedSubGroup: string | null,
    setSelectedSubGroup: React.Dispatch<React.SetStateAction<string | null>>,
    saveState: () => void;
}

const AdditionalAssortment = ({ selectedProduct, isExpanded, setIsExpanded, selectedMainGroup, setSelectedMainGroup, selectedSubGroup, setSelectedSubGroup, saveState }: AdditionalAssortmentProps) => {
    const { isMedium } = useDevice();
    const PAGE_SIZE = 10;
    const [size, setSize] = useState<number>(0);
    const [pageNumber, setPageNumber] = useState<number>(0);
    const [cartMessage, setCartMessage] = useState<string>('');
    const [summaryPriceAdditionalAssortment, setSummaryPriceAdditionalAssortment] = useState<string>();
    const { setShowCartMessage } = useCartMessage();
    const productList = useSelector((state: AppState) => state.data.orders.groupsAndProducts.products);
    const showNotification = useNotification();
    const showCartMessage = useSelector((state: AppState) => state.data.orders.showCartMessage);
    const cartTableContent = useSelector((state: AppState) => state.data.orders.cartTableContent);
    const isAdditionalDataFetching = useSelector((state: AppState) => state.data.orders.props.isAdditionalAssortmentInfoFetching);
    const [additionalProductList, setAdditionalProductList] = useState<AdditionalProductGrouped[]>([]);
    const [additionalAssortmentQuantity, setAdditionalAssortmentQuantity] = useState(
        Array<number>(selectedProduct.additionalAssortment?.length ?? 0).fill(0)
    );

    const [expandedMobileMenu, setExpandedMobileMenu] = useState<boolean>(false);
    const [activeKey, setActiveKey] = useState<string | null>(`${selectedMainGroup}-${selectedSubGroup}`);

    const additionalAssortmentInCart = useMemo(() => _.map(_.find(cartTableContent.additionalItems, (item, key) => key === selectedProduct.symKar), (grp) => grp.products).flat(), [selectedProduct, cartTableContent]);

    const [productAddedToCart, setProductAddedToCart] = useState<boolean>(false);

    const getUnitPriceAfterDiscount = (price: number, discount: number, converterM2: number) => (((100 - discount) / 100) * price) / (converterM2 === 0 ? 1 : converterM2);

    const getTotalPriceAfterDiscount = (count: number, price: number, discount: number, converterM2: number, dimensionDefault: number, isDimension: boolean) => {
        const unitPrice = getUnitPriceAfterDiscount(price, discount, converterM2);
        return count * unitPrice * ((isDimension ? dimensionDefault : 1) * (converterM2 === 0 ? 1 : converterM2));
    };

    useEffect(() => {
        if (!selectedSubGroup || !selectedMainGroup) return;
        setAdditionalProductList(selectedProduct.additionalAssortment.map((prd, originalIndex) => ({ prd, originalIndex })).filter(({ prd }) => prd.group1 === selectedMainGroup && prd.group2 === selectedSubGroup).reduce((acc: AdditionalProductGrouped[], { prd, originalIndex }) => {
            const { symKar, isDimension, dimensionDefault, unit, price, discount, converterM2, isDefault, ...rest } = prd;

            const existing = acc.find((p) => p.symKar === symKar);

            if (existing) {
                existing.units.push({ productIndex: originalIndex, isDimension, dimensionDefault, unit, price, discount, converterM2, isDefault });
            } else {
                acc.push({
                    symKar,
                    ...rest,
                    units: [{ productIndex: originalIndex, isDimension, dimensionDefault, unit, price, discount, converterM2, isDefault }]
                });
            }
            return acc;
        }, []));
    }, [selectedSubGroup, selectedProduct]);

    useEffect(() => {
        const groupTokens = activeKey?.split('-');
        if (groupTokens) {
            setSelectedMainGroup(groupTokens[0]);
            setSelectedSubGroup(groupTokens[1]);
        }
    }, [activeKey]);

    useEffect(() => {
        setSize(Math.max(1, _.ceil(additionalProductList.length / PAGE_SIZE)));
        setPageNumber(0);
    }, [additionalProductList]);

    useEffect(() => {
        if (!selectedSubGroup) {
            setIsExpanded(false);
            setActiveKey(null);
        }
    }, [selectedProduct]);

    useEffect(() => {
        setAdditionalAssortmentQuantity(Array<number>(selectedProduct.additionalAssortment?.length ?? 0).fill(0));
    }, [selectedProduct.additionalAssortment]);

    useEffect(() => {
        if (productAddedToCart) {
            _.map(selectedProduct.additionalAssortment, (prd, index) => {
                const prdInCart = _.find(additionalAssortmentInCart, (assInCart) => assInCart.product.symKar === prd.symKar);
                if (prdInCart === undefined && additionalAssortmentQuantity[index] > 0) {
                    const productDetails = _.find(productList, (productFromList) => productFromList.symKar === prd.symKar);
                    if (productDetails) {
                        store.dispatch(dataActions.addToLocalCart({
                            product: {
                                product: productDetails as Product,
                                dimension: productDetails.dimensionDefault ?? 1,
                                quantity: additionalAssortmentQuantity[index],
                                additionsSelected: _.map(productDetails.additions, (add) => add.default),
                                mainProductSymkar: selectedProduct.symKar,
                                unit: cartMethods.getDefaultUnitOfProduct(productDetails),
                                cartGroup: CartGroup.main,
                                systemData: null
                            }
                        }));
                    }
                } else if (prdInCart && additionalAssortmentQuantity[index] > 0) {
                    store.dispatch(dataActions.editProductsInLocalCart({
                        index: prdInCart.indexInCart,
                        product: {
                            ...prdInCart,
                            quantity: prdInCart.quantity + additionalAssortmentQuantity[index]
                        }
                    }));
                }
            });
            setCartMessage('Dodano asortyment dodatkowy do koszyka');
            if (isMedium) setShowCartMessage(Messages.additionalAssortmentMessage);
            showNotification('Dodano asortyment dodatkowy do koszyka');
            setProductAddedToCart(false);
            setAdditionalAssortmentQuantity(Array<number>(selectedProduct.additionalAssortment?.length ?? 0).fill(0));
        }
    }, [productAddedToCart]);

    useEffect(() => {
        let sum = 0;
        for (let index = 0; index < selectedProduct.additionalAssortment.length; ++index) {
            const count = additionalAssortmentQuantity[index];
            const productData = selectedProduct.additionalAssortment[index];
            sum += getTotalPriceAfterDiscount(count, productData.price, productData.discount, productData.converterM2, productData.dimensionDefault, productData.isDimension);
        }
        setSummaryPriceAdditionalAssortment(sum.toFixed(2));
    }, [additionalAssortmentQuantity]);

    const addAdditionalToCart = async () => {
        const productsToQuery = _.map(
            _.filter(selectedProduct.additionalAssortment, (addition, index) => additionalAssortmentQuantity[index] > 0 && !productList.some((product) => product.symKar === addition.symKar)),
            (addition) => ({ symKar: addition.symKar, unit: addition.unit })
        );

        if (productsToQuery.length > 0) {
            await getProductWithPrice(productsToQuery, true);
        }
        setProductAddedToCart(true);
    };

    const handleClearAdditionalAssortmentQuantity = () => {
        setAdditionalAssortmentQuantity(Array<number>(selectedProduct.additionalAssortment?.length ?? 0).fill(0));
        setCartMessage('Wyczyszczono asortyment dodatkowy');
        if (isMedium) setShowCartMessage(Messages.additionalAssortmentMessage);
        showNotification('Wyczyszczono asortyment dodatkowy');
    };

    return (
        <>
            {selectedProduct.additionalAssortment.length !== 0 &&
                <>
                    <div
                        className={`AdditionalAssortment-dropdown-button ${isExpanded ? 'expanded' : ''}`}
                        onClick={() => {
                            setIsExpanded(!isExpanded);
                            setActiveKey(`${selectedProduct.additionalAssortment[0].group1}-${selectedProduct.additionalAssortment[0].group2}`);
                        }}
                    >
                        <Image src={buildResourceLink('/storage/groups/TOW.png')} style={{ height: 'inherit' }} />
                        <div style={{ fontWeight: 'bold', fontSize: '20', alignContent: 'center' }}>
                            Dodaj asortyment dodatkowy
                        </div>
                        {isExpanded ? <FaCaretUp /> : <FaCaretDown />}
                    </div>
                    {
                        isMedium ?
                            <Collapse in={isExpanded}>
                                <div className='AdditionalAssortment-additionalAssortmentGrid' style={{ gridColumn: 'span 2', cursor: 'default', gridTemplateColumns: selectedMainGroup !== null ? '15% 85%' : 'auto 1fr' }}>
                                    <div className='AdditionalAssortment-menu AdditionalAssortment-menu-top'>
                                        {[...new Set(selectedProduct.additionalAssortment.map((item) => item.group1))].map((group) => (
                                            <div
                                                key={group}
                                                style={{ alignContent: 'center' }}
                                                className={selectedMainGroup === group ? 'selected' : ''}
                                                onClick={() => {
                                                    setSelectedMainGroup(group);
                                                    setSelectedSubGroup(selectedProduct.additionalAssortment.filter((prd) => prd.group1 === group)[0].group2);
                                                    setPageNumber(0);
                                                }}
                                            >
                                                {group === 'Obróbki blacharskie' ?
                                                    <>
                                                        {group}
                                                        <OverlayTrigger
                                                            key='top'
                                                            placement='top'
                                                            overlay={
                                                                <Popover className='AdditionalAssortment-dark-popover' id='popover-positioned-top'>
                                                                    <span>W celu zamówienia obróbek w nano-fali, skontaktuj się z opiekunem lub zaznacz to w uwagach do zamówienia</span>
                                                                </Popover>
                                                            }
                                                        >
                                                            <span>*</span>
                                                        </OverlayTrigger>
                                                    </> :
                                                    <span>{group}</span>}
                                            </div>
                                        ))}
                                    </div>
                                    <MenuScroll parentClass='AdditionalAssortment-menu-side' elementClass='AdditionalAssortment-menu AdditionalAssortment-menu-side-elements'>
                                        {[...new Set(selectedProduct.additionalAssortment.filter((prd) => prd.group1 === selectedMainGroup).map((item) => item.group2))].map((group) => (
                                            <div
                                                key={group}
                                                className={selectedSubGroup === group ? 'selected' : ''}
                                                onClick={() => {
                                                    setSelectedSubGroup(group);
                                                }}
                                            >
                                                {group.toUpperCase()}
                                            </div>
                                        ))}
                                    </MenuScroll>

                                    {selectedMainGroup !== null &&
                                        <>
                                            <div className='AdditionalAssortment-content'>
                                                <div className='AdditionalAssortment-product-list'>
                                                    {additionalProductList.slice(PAGE_SIZE * pageNumber, PAGE_SIZE * pageNumber + PAGE_SIZE).map((prd) => (
                                                        <AdditionalAssortmentRow
                                                            onClick={saveState}
                                                            key={prd.symKar}
                                                            product={prd}
                                                            calculateUnitPrice={getUnitPriceAfterDiscount}
                                                            calculateTotalPrice={getTotalPriceAfterDiscount}
                                                            assortmentQuantity={additionalAssortmentQuantity}
                                                            setAdditionalAssortmentQuantity={setAdditionalAssortmentQuantity}
                                                        />
                                                    ))}
                                                </div>
                                                <div className='AdditionalAssortment-list-pagination'>
                                                    <Pagination
                                                        selected={pageNumber}
                                                        size={size}
                                                        onSelect={(p) => setPageNumber(p)}
                                                    />
                                                </div>
                                            </div>
                                            <div className='AdditionalAssortment-content-summary'>
                                                <div className='AdditionalAssortment-price-total-wrapper'>
                                                    <div>
                                                        ASORTYMENT DODATKOWY - SUMA:
                                                    </div>
                                                    <div className='price-color AdditionalAssortment-price'>
                                                        {summaryPriceAdditionalAssortment}
                                                    </div>
                                                </div>
                                                <div className='AdditionalAssortment-button-wrapper'>
                                                    <div className={`AdditionalAssortment-cartMessage ${showCartMessage === Messages.additionalAssortmentMessage ? 'show' : ''}`} style={{ height: '42px' }}>
                                                        {cartMessage}
                                                    </div>
                                                    <ButtonComponent
                                                        text='WYCZYŚĆ'
                                                        variant='danger'
                                                        marginRight='5px'
                                                        brandColoredButton={false}
                                                        onClick={handleClearAdditionalAssortmentQuantity}
                                                    />
                                                    <ButtonComponent
                                                        text='DODAJ DO KOSZYKA'
                                                        variant='success'
                                                        marginRight='0'
                                                        marginLeft='0'
                                                        brandColoredButton={false}
                                                        onClick={addAdditionalToCart}
                                                        isSpinner={isAdditionalDataFetching}
                                                        disabled={!additionalAssortmentQuantity.some((quantity) => quantity > 0)}
                                                    />
                                                </div>
                                            </div>
                                        </>}
                                </div>
                            </Collapse> :
                            <div style={{ position: 'relative' }}>
                                {isExpanded &&
                                    <Navbar className='AdditionalAssortment-navHeader' variant='dark' expand='lg' collapseOnSelect onSelect={(val) => setActiveKey(val)} onToggle={() => setExpandedMobileMenu((val) => !val)} expanded={expandedMobileMenu}>
                                        <Container>
                                            <div className='AdditionalAssortment-navHeader-content' onClick={() => setExpandedMobileMenu((val) => !val)}>
                                                <p className='AdditionalAssortment-navHeader-title'>{selectedSubGroup?.toUpperCase()}</p>
                                                <div className='AdditionalAssortment-navHeader-toggle'>
                                                    {!expandedMobileMenu && <div><FaCaretDown /></div>}
                                                    {expandedMobileMenu && <div><FaCaretUp /></div>}
                                                </div>
                                            </div>
                                            <hr className='AdditionalAssortment-nav-splitter' />
                                            <Navbar.Collapse style={{ width: 'stretch' }}>
                                                <Nav fill variant='pills' activeKey={activeKey ?? undefined} className='mr-auto'>
                                                    {[...new Set(selectedProduct.additionalAssortment.map((item) => item.group1))].map((group, index, arr) => (
                                                        <>
                                                            <NavDropdown
                                                                renderMenuOnMount
                                                                title={group}
                                                                id={group}
                                                                className='AdditionalAssortment-navDropdown'
                                                            >
                                                                {[...new Set(selectedProduct.additionalAssortment.filter((prd) => prd.group1 === group).map((item) => item.group2))].map((group2, index2, arr2) => (
                                                                    <>
                                                                        <NavDropdown.Item eventKey={`${group}-${group2}`} className={activeKey === `${group}-${group2}` ? 'AdditionalAssortment-navDropdownItem-active' : 'AdditionalAssortment-navDropdownItem'}>
                                                                            {group2.toUpperCase()}
                                                                        </NavDropdown.Item>
                                                                        {index2 !== arr2.length - 1 && <hr className='AdditionalAssortment-nav-splitter' />}
                                                                    </>
                                                                ))}
                                                            </NavDropdown>
                                                            {index !== arr.length - 1 && <hr className='AdditionalAssortment-nav-splitter' />}
                                                        </>
                                                    ))}
                                                </Nav>
                                            </Navbar.Collapse>
                                        </Container>
                                    </Navbar>}
                                <Collapse in={isExpanded} timeout={0}>
                                    <div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                            {additionalProductList.map((prd) => (
                                                <AdditionalAssortmentRow
                                                    onClick={saveState}
                                                    key={prd.symKar}
                                                    product={prd}
                                                    calculateUnitPrice={getUnitPriceAfterDiscount}
                                                    calculateTotalPrice={getTotalPriceAfterDiscount}
                                                    assortmentQuantity={additionalAssortmentQuantity}
                                                    setAdditionalAssortmentQuantity={setAdditionalAssortmentQuantity}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </Collapse>
                                {isExpanded &&
                                    <div style={{ position: 'sticky', bottom: 0 }}>
                                        <div className='AdditionalAssortment-content-summary'>
                                            <div className='AdditionalAssortment-price-total-wrapper'>
                                                <div>
                                                    ASORTYMENT DODATKOWY - SUMA:
                                                </div>
                                                <div className='price-color AdditionalAssortment-price'>
                                                    {summaryPriceAdditionalAssortment}
                                                </div>
                                            </div>
                                            <div className='AdditionalAssortment-button-wrapper'>
                                                <ButtonComponent
                                                    text='WYCZYŚĆ'
                                                    variant='danger'
                                                    marginRight='5px'
                                                    brandColoredButton={false}
                                                    onClick={handleClearAdditionalAssortmentQuantity}
                                                />
                                                <ButtonComponent
                                                    text='DODAJ DO KOSZYKA'
                                                    variant='success'
                                                    marginRight='0'
                                                    marginLeft='0'
                                                    brandColoredButton={false}
                                                    onClick={addAdditionalToCart}
                                                    isSpinner={isAdditionalDataFetching}
                                                    disabled={!additionalAssortmentQuantity.some((quantity) => quantity > 0)}
                                                />
                                            </div>
                                        </div>
                                    </div>}
                            </div>
                    }
                </>}
        </>);
};

export default AdditionalAssortment;
